/**
 * PodLayout — dedicated layout for the SBL Leadership POD area.
 *
 * Independent of the LMS Layout. POD subscribers (and super_admins / users with
 * pod_access) navigate here without ever seeing LMS items like Programmes,
 * Browse Programmes, Learning Progress, Courses, etc.
 */
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { House, Megaphone, Files, Sparkle, UsersThree, ChatCircle, User, SignOut, Bell } from "@phosphor-icons/react";

const POD_NAV = [
  { label: "POD Home", icon: House, path: "/pod/dashboard" },
  { label: "Webinars", icon: Megaphone, path: "/pod/webinars" },
  { label: "Resources", icon: Files, path: "/pod/resources" },
  { label: "AI Templates", icon: Sparkle, path: "/pod/ai-templates" },
  { label: "Roundtables", icon: UsersThree, path: "/pod/roundtable" },
  { label: "POD Forum", icon: ChatCircle, path: "/pod/forum" },
  { label: "My Profile", icon: User, path: "/pod/profile" },
];

export default function PodLayout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const onLogout = async () => { await logout(); navigate("/pod"); };

  return (
    <div className="min-h-screen flex bg-slate-50">
      <aside className="hidden md:flex md:flex-col md:w-64 bg-slate-950 text-white">
        <div className="px-6 py-6 border-b border-white/10">
          <p className="text-[10px] uppercase tracking-[0.25em] text-amber-400 font-bold">SBL Leadership</p>
          <p className="text-2xl font-extrabold mt-1" style={{ fontFamily: "'Outfit', sans-serif" }}>POD</p>
          <p className="text-[11px] text-white/60 mt-0.5">Professional Community</p>
        </div>
        <nav className="flex-1 py-4 px-3 space-y-1">
          {POD_NAV.map(it => {
            const Icon = it.icon;
            const active = location.pathname === it.path || (it.path !== "/pod/dashboard" && location.pathname.startsWith(it.path));
            return (
              <Link
                key={it.path}
                to={it.path}
                className={`flex items-center gap-3 px-3 py-2.5 text-sm transition-all border-l-2 ${active ? "bg-white/10 text-amber-400 border-amber-400" : "text-white/80 hover:bg-white/5 hover:text-white border-transparent"}`}
                data-testid={`pod-nav-${it.path.replace("/pod/", "").replace("/pod", "home")}`}
              >
                <Icon size={18} weight={active ? "fill" : "regular"} />
                {it.label}
              </Link>
            );
          })}
          <Link to="/dashboard" className="flex items-center gap-2 px-3 py-2 text-xs text-white/50 hover:text-white hover:bg-white/5 mt-2 border-t border-white/10 pt-3" data-testid="pod-return-dashboard">
            ← Return to Dashboard
          </Link>
        </nav>
        <div className="px-3 py-4 border-t border-white/10">
          <div className="flex items-center gap-3 px-3 py-2 mb-2">
            <div className="w-8 h-8 rounded-full bg-amber-400 text-slate-950 flex items-center justify-center text-xs font-bold">{(user?.name || "?").slice(0, 1).toUpperCase()}</div>
            <div className="min-w-0">
              <p className="text-xs font-semibold truncate">{user?.name}</p>
              <p className="text-[10px] text-white/50 truncate">{user?.email}</p>
            </div>
          </div>
          <button onClick={onLogout} className="w-full flex items-center gap-2 px-3 py-2 text-xs text-white/70 hover:text-white hover:bg-white/5 transition-all" data-testid="pod-logout-btn">
            <SignOut size={14} /> Sign out
          </button>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="bg-white border-b border-slate-200 px-4 md:px-6 py-3 flex items-center justify-between sticky top-0 z-30">
          <div className="md:hidden">
            <p className="text-[10px] uppercase tracking-widest text-amber-600 font-bold">SBL POD</p>
            <p className="text-sm font-bold text-slate-950">Leadership Community</p>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <Link to="/pod/notifications" className="p-2 text-slate-500 hover:text-slate-950 transition-colors" data-testid="pod-bell">
              <Bell size={20} />
            </Link>
          </div>
        </header>
        <main className="flex-1">{children}</main>
      </div>
    </div>
  );
}
